import { Model } from "../Model";
import { observeArray, observeProperties } from "../Observer";
import { Subscription } from "../Signal";
import { Template } from "../view/Template";
import { View } from "../view/View";
import { Scope } from "./Scope";


export class Repeat<Type extends RepeatModel = RepeatModel> extends View<Type> implements Scope {

	protected itemTemplate: string;
	protected arraySubscription: Subscription;
	protected dataSubscription: Subscription;

	protected cache: Map<any, Template> = new Map();

	constructor(config?) {
		super({ template: '', data: new RepeatModel(), ...config });
	}

	prepareContent(node: Element, template: Template) {
		this.itemTemplate = node.innerHTML;
	}

	init() {
		this.onDataChange();
	}

	deinit() {
		this.unsubscribe();


		// destroy all templates.
		for (const template of this.cache.values() ) {
			template.destroy();
		}
	}

	onDataChange() {
		this.unsubscribe();
		this.subscribe();

		this.renderItems();
	}


	renderItems() {

		// check for valid array
		let items = this.data.items;
		if (!Array.isArray(items)) items = [];

		// create new cache.
		let existing = this.cache;
		this.cache = new Map();

		items.forEach((item, index) => {

			let cacheKey = this.data.getCacheKey( item );

			// check if template is already rendered
			let template = existing.get( cacheKey );

			// no already rendered template - create it
			if( !template ) {
				template = this.createTemplate(items, item, index);

			} else {
				// remove from existing cache to prevent removing template
				existing.delete( cacheKey );
			}

			this.cache.set( cacheKey, template );

			// adjust template order in node if its not already there
			if( this.node.childNodes?.[ index ] != template.node ) {
				this.node.insertBefore( template.node, this.node.childNodes?.[ index ] );
			}
		});

		// remove old templates
		existing.forEach( ( template, item ) => {
			template.node?.parentNode?.removeChild( template.node );
			template.destroy();
		});
	}





	createTemplate(items: Array<any>, item: any, index: number) {

		// create template if not in cache
		let template = new Template(this.itemTemplate);
		let data = {
			parent: this.template.context.parent.data,
			index: index,
			items: items,
			isFirst: item == items[0],
			isLast: item == items[items.length - 1],
		};

		// inject item
		data[this.data.as] = item;

		template.context.data = data;
		template.extendContext(this.template.context);

		template.render();

		return template;
	}

	/**
	 * Subscribes to the data
	 */
	subscribe() {
		this.dataSubscription = observeProperties(this.data, 'items')?.propertyChange.filter(c => c.name == 'items').subscribe(this.onDataChange.bind(this));
		this.arraySubscription = observeArray(this.data.items)?.arrayChange.subscribe(this.onDataChange.bind(this));
	}

	unsubscribe() {
		this.dataSubscription?.unsubscribe();
		this.arraySubscription?.unsubscribe();
	}
}


interface CacheKey {
	(data: any): any;
}

export class RepeatModel extends Model {

	as: string;
	items: any[];
	caching: boolean;

	constructor(config?) {
		super({ as: 'item', items: [], caching: true, ...config });
	}

	/**
	 * A setter to allow to set the length of the loop by number
	 */
	set count(num: number) {
		this.items = Array.from(Array(num).keys());
	}

	getCacheKey(item: any) {
		if (this.caching === true && typeof item == 'object' ) return item;
		// create a new object for every item to have a unique key.
		return { key:item };
	}
}

